"use client";

import { c as _c } from "react/compiler-runtime";
import { IconChevronLeftMedium } from "central-icons/IconChevronLeftMedium";
import { IconChevronRightMedium } from "central-icons/IconChevronRightMedium";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useState } from "react";
import ConcentricBorderRadiusDemo from "@/components/demos/concentric-border-radius";
import FontSmoothingDemo from "@/components/demos/font-smoothing";
import ImageBorderDemo from "@/components/demos/image-border";
import InterruptibleRotationDemo from "@/components/demos/interruptible-rotation";
import TabularNumbersDemo from "@/components/demos/tabular-numbers";
import TextWrapDemo from "@/components/demos/text-wrap";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import AnimateIconsView from "./animate-icons";
import ExitAnimationView from "./exit-animation";
import FadeInView from "./fade-in";
import OpticalAlignmentView from "./optical-alignment";
import ShadowsView from "./shadows";
import { SLIDE_HEIGHT } from "./slide-height";
const slides = [{
  content: <TextWrapDemo defaultWidth={260} descriptionWrap="pretty" fill height={SLIDE_HEIGHT} label="balance + pretty" titleWrap="balance" />,
  key: "text-wrap"
}, {
  content: <ConcentricBorderRadiusDemo height={SLIDE_HEIGHT} />,
  key: "concentric"
}, {
  content: <TabularNumbersDemo height={SLIDE_HEIGHT} />,
  key: "tabular-nums"
}, {
  content: <AnimateIconsView />,
  key: "animate-icons"
}, {
  content: <ShadowsView />,
  key: "shadows"
}, {
  content: <FontSmoothingDemo height={SLIDE_HEIGHT} panelClassName="px-8" />,
  key: "font-smoothing"
}, {
  content: <OpticalAlignmentView />,
  key: "optical-alignment"
}, {
  content: <InterruptibleRotationDemo height={SLIDE_HEIGHT} />,
  key: "interruptible-rotation"
}, {
  content: <FadeInView />,
  key: "fade-in"
}, {
  content: <ExitAnimationView />,
  key: "exit-animation"
}, {
  content: <ImageBorderDemo height={SLIDE_HEIGHT} />,
  key: "image-border"
}];
function isInWindow(index: number, selected: number, total: number) {
  if (index === selected) {
    return true;
  }
  const next = (selected + 1) % total;
  const prev = (selected - 1 + total) % total;
  return index === next || index === prev;
}
export default function DemoCarousel() {
  const $ = _c(32);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      align: "center",
      containScroll: false,
      loop: true,
      watchDrag: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  let t2;
  if ($[1] !== emblaApi) {
    t1 = () => {
      if (!emblaApi) {
        return;
      }
      const sync = () => setSelectedIndex(emblaApi.selectedScrollSnap());
      sync();
      emblaApi.on("select", sync);
      emblaApi.on("reInit", sync);
      return () => {
        emblaApi.off("select", sync);
        emblaApi.off("reInit", sync);
      };
    };
    t2 = [emblaApi];
    $[1] = emblaApi;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const t3 = emblaApi ? "opacity-100" : "opacity-0";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("overflow-hidden px-5 sm:px-0 sm:py-0", "transition-opacity duration-100", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== selectedIndex) {
    t5 = slides.map((slide, i) => {
      const inWindow = isInWindow(i, selectedIndex, slides.length);
      return <div className="min-w-0 flex-[0_0_101%] pl-5 sm:flex-[0_0_calc(100%-160px)]" key={slide.key}>{inWindow ? slide.content : <DemoWrapper height={SLIDE_HEIGHT}>{null}</DemoWrapper>}</div>;
    });
    $[6] = selectedIndex;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <div className="-ml-5 flex items-center">{t5}</div>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== emblaRef || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className={t4} ref={emblaRef}>{t6}</div>;
    $[10] = emblaRef;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-16 bg-linear-to-r from-gray-background to-transparent sm:block" />;
    t9 = <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-16 bg-linear-to-l from-gray-background to-transparent sm:block" />;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[16] !== emblaApi) {
    t10 = () => emblaApi?.scrollPrev();
    $[16] = emblaApi;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <IconChevronLeftMedium className="size-4.5" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10) {
    t12 = <IconButton aria-label="Previous slide" className="text-gray-1100 transition-colors duration-200 ease-out hover:text-gray-1200" onClick={t10} rounded={true} size="xs" variant="secondary">{t11}</IconButton>;
    $[19] = t10;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== emblaApi) {
    t13 = () => emblaApi?.scrollNext();
    $[21] = emblaApi;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <IconChevronRightMedium className="size-4.5" />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13) {
    t15 = <IconButton aria-label="Next slide" className="text-gray-1100 transition-colors duration-200 ease-out hover:text-gray-1200" onClick={t13} rounded={true} size="xs" variant="secondary">{t14}</IconButton>;
    $[24] = t13;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t15) {
    t16 = <div className="mt-3 flex items-center justify-center gap-3 sm:mt-0">{t12}{t15}</div>;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16 || $[30] !== t7) {
    t17 = <div className="relative -mx-5 sm:-mx-20">{t7}{t8}{t9}{t16}</div>;
    $[29] = t16;
    $[30] = t7;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  return t17;
}